import { useState, useEffect } from "react";
import { useAuth } from "@/App";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
  BarChart3, 
  CheckCircle, 
  TrendingUp, 
  Users, 
  ChevronLeft, 
  ChevronRight, 
  Maximize2, 
  Minimize2 
} from "lucide-react";
import { toast } from "sonner";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

const QADashboardPage = () => {
  const { authAxios, user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [layout, setLayout] = useState([]);

  const defaultLayout = [
    { id: "evaluators-count", name: "Evaluaciones por Analista", size: "medium" },
    { id: "evaluators-score", name: "Calificación Promedio Asignada", size: "medium" },
    { id: "categories", name: "Motivos de Interacción (Categorización)", size: "medium" },
    { id: "reasons", name: "Causas de Falla del Agente", size: "medium" },
    { id: "areas", name: "Áreas de Falla Frecuentes", size: "medium" },
  ];

  const fetchStats = async () => {
    try {
      setLoading(true);
      const res = await authAxios.get("/qa/stats");
      setStats(res.data);
    } catch (error) {
      console.error("Error fetching QA stats:", error);
      toast.error("Error al cargar métricas de QA");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    const userKey = user ? `qa_layout_${user.email}` : "qa_layout_default";
    const saved = localStorage.getItem(userKey);
    if (saved) {
      try {
        setLayout(JSON.parse(saved));
      } catch (e) {
        setLayout(defaultLayout);
      }
    } else {
      setLayout(defaultLayout);
    }
  }, [user]);

  const saveLayout = (newLayout) => {
    setLayout(newLayout);
    const userKey = user ? `qa_layout_${user.email}` : "qa_layout_default";
    localStorage.setItem(userKey, JSON.stringify(newLayout));
  };

  const moveWidget = (idx, direction) => {
    const newLayout = [...layout];
    const targetIdx = idx + direction;
    if (targetIdx >= 0 && targetIdx < newLayout.length) {
      const temp = newLayout[idx];
      newLayout[idx] = newLayout[targetIdx];
      newLayout[targetIdx] = temp;
      saveLayout(newLayout);
    }
  };

  const toggleSize = (idx) => {
    const newLayout = [...layout];
    newLayout[idx].size = newLayout[idx].size === "medium" ? "large" : "medium";
    saveLayout(newLayout);
  };

  const KPICard = ({ title, value, subtitle, icon: Icon, color = "sky" }) => (
    <Card className="kpi-card shadow-sm border-slate-100 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium text-slate-500">{title}</p>
          <p className={`text-3xl font-bold mt-2 text-${color}-600`}>{value}</p>
          {subtitle && <p className="text-sm text-slate-400 mt-1">{subtitle}</p>}
        </div>
        <div className={`w-12 h-12 rounded-xl bg-${color}-100 flex items-center justify-center`}>
          <Icon className={`w-6 h-6 text-${color}-600`} />
        </div>
      </div>
    </Card>
  );

  const renderWidgetContent = (id, isLarge) => {
    const heightClass = isLarge ? "h-[400px]" : "h-[300px]";
    
    switch (id) {
      case "evaluators-count": {
        const data = (stats?.by_evaluator || [])
          .sort((a, b) => (b.calls_evaluated || 0) - (a.calls_evaluated || 0))
          .slice(0, 10);
        return (
          <div className={heightClass}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
                <XAxis type="number" />
                <YAxis dataKey="_id" type="category" width={110} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar 
                  dataKey="calls_evaluated" 
                  name="Evaluaciones" 
                  fill="#0EA5E9" 
                  radius={[0, 4, 4, 0]} 
                  className="cursor-pointer"
                  onClick={(d) => {
                    if (d && d._id) {
                      navigate(`/calls?search=${encodeURIComponent(d._id)}`);
                    }
                  }}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        );
      }
      case "evaluators-score": {
        const data = (stats?.by_evaluator || [])
          .sort((a, b) => (b.avg_score || 0) - (a.avg_score || 0))
          .slice(0, 10);
        return (
          <div className={heightClass}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
                <XAxis type="number" domain={[0, 100]} />
                <YAxis dataKey="_id" type="category" width={110} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar 
                  dataKey="avg_score" 
                  name="Calificación Promedio" 
                  fill="#10B981" 
                  radius={[0, 4, 4, 0]} 
                  className="cursor-pointer"
                  onClick={(d) => {
                    if (d && d._id) {
                      navigate(`/calls?search=${encodeURIComponent(d._id)}`);
                    }
                  }}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        );
      }
      case "categories": {
        const data = (stats?.categories || [])
          .sort((a, b) => (b.value || 0) - (a.value || 0))
          .slice(0, 10);
        return (
          <div className={heightClass}>
            {(!stats?.categories || stats.categories.length === 0) ? (
              <p className="text-sm text-slate-500 text-center py-24">No hay datos de categorización.</p>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
                  <XAxis type="number" />
                  <YAxis dataKey="name" type="category" width={110} tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar 
                    dataKey="value" 
                    name="Frecuencia" 
                    fill="#0EA5E9" 
                    radius={[0, 4, 4, 0]} 
                    className="cursor-pointer"
                    onClick={(d) => {
                      if (d && d.name) {
                        navigate(`/calls?search=${encodeURIComponent(d.name)}`);
                      }
                    }}
                  />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        );
      }
      case "reasons": {
        const data = (stats?.failure_reasons || [])
          .sort((a, b) => (b.value || 0) - (a.value || 0))
          .slice(0, 10);
        return (
          <div className={heightClass}>
            {(!stats?.failure_reasons || stats.failure_reasons.length === 0) ? (
              <p className="text-sm text-slate-500 text-center py-24">No hay fallas registradas.</p>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                {isLarge ? (
                  <BarChart data={data}>
                    <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
                    <XAxis 
                      dataKey="name" 
                      tick={{ fontSize: 9 }} 
                      angle={-25} 
                      textAnchor="end" 
                      height={70} 
                      interval={0} 
                    />
                    <YAxis />
                    <Tooltip />
                    <Bar 
                      dataKey="value" 
                      name="Casos" 
                      fill="#EF4444" 
                      radius={[4, 4, 0, 0]} 
                      className="cursor-pointer"
                      onClick={(d) => {
                        if (d && d.name) {
                          navigate(`/calls?search=${encodeURIComponent(d.name)}`);
                        }
                      }}
                    />
                  </BarChart>
                ) : (
                  <BarChart data={data} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
                    <XAxis type="number" />
                    <YAxis dataKey="name" type="category" width={110} tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Bar 
                      dataKey="value" 
                      name="Casos" 
                      fill="#EF4444" 
                      radius={[0, 4, 4, 0]} 
                      className="cursor-pointer"
                      onClick={(d) => {
                        if (d && d.name) {
                          navigate(`/calls?search=${encodeURIComponent(d.name)}`);
                        }
                      }}
                    />
                  </BarChart>
                )}
              </ResponsiveContainer>
            )}
          </div>
        );
      }
      case "areas": {
        const data = (stats?.failure_areas || [])
          .sort((a, b) => (b.value || 0) - (a.value || 0))
          .slice(0, 15);
        return (
          <div className={`${heightClass} overflow-y-auto pr-2`}>
            {(!stats?.failure_areas || stats.failure_areas.length === 0) ? (
              <p className="text-sm text-slate-500 text-center py-24">No hay áreas de falla registradas.</p>
            ) : (
              <div className="space-y-4">
                {data.map((area, idx) => (
                  <div 
                    key={idx} 
                    onClick={() => navigate(`/calls?search=${encodeURIComponent(area.name)}`)}
                    className="flex justify-between items-center p-3 bg-slate-50 border border-slate-100 rounded-lg hover:bg-slate-100/60 transition-colors cursor-pointer hover:border-sky-200 shadow-2xs"
                  >
                    <span className="text-sm font-semibold text-slate-700 capitalize">{area.name}</span>
                    <span className="text-xs font-bold text-rose-700 bg-rose-50 px-2.5 py-1 rounded-full">
                      {area.value} {area.value === 1 ? 'caso' : 'casos'}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      }
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-3xl font-bold text-slate-900">Dashboard de Calidad</h1>
        <p className="text-slate-500 mt-1">Métricas de evaluación manual y desempeño de analistas de calidad.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <KPICard
          title="Llamadas Evaluadas"
          value={stats?.total_evaluated || 0}
          subtitle={`De un total de ${stats?.total_calls || 0} llamadas`}
          icon={CheckCircle}
          color="emerald"
        />
        <KPICard
          title="Tasa de Evaluación"
          value={`${stats?.evaluation_rate || 0}%`}
          subtitle="Porcentaje del total de llamadas"
          icon={TrendingUp}
          color="sky"
        />
        <KPICard
          title="Evaluadores Activos"
          value={stats?.by_evaluator?.length || 0}
          subtitle="Analistas que han evaluado"
          icon={Users}
          color="purple"
        />
      </div>

      {/* Render Dynamic Layout Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {layout.map((widget, idx) => {
          const isLarge = widget.size === "large";
          const colSpan = isLarge ? "lg:col-span-2" : "col-span-1";
          
          return (
            <Card key={widget.id} className={`${colSpan} transition-all duration-300 shadow-sm border-slate-100 hover:shadow-md`}>
              <CardHeader className="flex flex-row items-center justify-between pb-3">
                <CardTitle className="text-lg font-semibold text-slate-800">{widget.name}</CardTitle>
                <div className="flex items-center gap-1 bg-slate-50 border border-slate-100 rounded-lg p-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="w-7 h-7 rounded hover:bg-slate-200 text-slate-500"
                    onClick={() => moveWidget(idx, -1)}
                    disabled={idx === 0}
                    title="Mover arriba / izquierda"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="w-7 h-7 rounded hover:bg-slate-200 text-slate-500"
                    onClick={() => moveWidget(idx, 1)}
                    disabled={idx === layout.length - 1}
                    title="Mover abajo / derecha"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="w-7 h-7 rounded hover:bg-slate-200 text-slate-500"
                    onClick={() => toggleSize(idx)}
                    title={isLarge ? "Reducir tamaño" : "Ampliar a ancho completo"}
                  >
                    {isLarge ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {renderWidgetContent(widget.id, isLarge)}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default QADashboardPage;
